import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from '@eliadmin/core/src/components/ui/sheet';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useRealtyStore } from '../../../stores/realty-store';
import { useId, useRef, useState, type KeyboardEvent } from 'react';
import type { RealtyOrder, RealtyOrderBy } from '../../../utils/realty-filter-contract';
import listingStyles from '../realty-listing.module.css';
import styles from './realty-sort.module.css';

const MobileSortIcon = () => (
  <svg
    aria-hidden="true"
    className={styles.mobileSortIcon}
    data-eli-part="listing-sort-icon"
    focusable="false"
    viewBox="0 0 24 24"
  >
    <path
      d="M7.52875 11C7.31625 11 7.13817 10.9281 6.9945 10.7843C6.85067 10.6404 6.77875 10.4623 6.77875 10.2498C6.77875 10.0371 6.85067 9.859 6.9945 9.7155C7.13817 9.57183 7.31625 9.5 7.52875 9.5H9.4615C9.674 9.5 9.85217 9.57192 9.996 9.71575C10.1397 9.85958 10.2115 10.0378 10.2115 10.2503C10.2115 10.4629 10.1397 10.641 9.996 10.7845C9.85217 10.9282 9.674 11 9.4615 11H7.52875ZM3.65375 6.25C3.44125 6.25 3.26317 6.17808 3.1195 6.03425C2.97583 5.89042 2.904 5.71225 2.904 5.49975C2.904 5.28708 2.97583 5.109 3.1195 4.9655C3.26317 4.82183 3.44125 4.75 3.65375 4.75H13.3365C13.549 4.75 13.7272 4.82192 13.871 4.96575C14.0147 5.10958 14.0865 5.28775 14.0865 5.50025C14.0865 5.71292 14.0147 5.891 13.871 6.0345C13.7272 6.17817 13.549 6.25 13.3365 6.25H3.65375ZM0.75 1.5C0.5375 1.5 0.359417 1.42808 0.21575 1.28425C0.0719168 1.14042 0 0.96225 0 0.74975C0 0.537083 0.0719168 0.359 0.21575 0.2155C0.359417 0.0718332 0.5375 0 0.75 0H16.25C16.4625 0 16.6406 0.0719168 16.7843 0.21575C16.9281 0.359583 17 0.53775 17 0.75025C17 0.962917 16.9281 1.141 16.7843 1.2845C16.6406 1.42817 16.4625 1.5 16.25 1.5H0.75Z"
      fill="currentColor"
      transform="translate(3.5 6.5)"
    />
  </svg>
);

const MobileCloseIcon = () => (
  <svg aria-hidden="true" className={styles.mobileCloseIcon} focusable="false" viewBox="0 0 24 24">
    <path
      d="M6.34425 7.398L1.27125 12.4713C1.13275 12.6096 0.958667 12.6804 0.749 12.6838C0.5395 12.6869 0.36225 12.6161 0.21725 12.4713C0.0724167 12.3263 0 12.1506 0 11.9443C0 11.7379 0.0724167 11.5623 0.21725 11.4173L5.2905 6.34425L0.21725 1.27125C0.0789167 1.13275 0.00808339 0.958667 0.00475006 0.749001C0.00158339 0.539501 0.0724167 0.36225 0.21725 0.21725C0.36225 0.0724167 0.537917 0 0.74425 0C0.950583 0 1.12625 0.0724167 1.27125 0.21725L6.34425 5.2905L11.4173 0.21725C11.5558 0.0789167 11.7298 0.00808339 11.9395 0.00475006C12.149 0.00158339 12.3263 0.0724167 12.4713 0.21725C12.6161 0.36225 12.6885 0.537917 12.6885 0.74425C12.6885 0.950584 12.6161 1.12625 12.4713 1.27125L7.398 6.34425L12.4713 11.4173C12.6096 11.5558 12.6804 11.7298 12.6838 11.9395C12.6869 12.149 12.6161 12.3263 12.4713 12.4713C12.3263 12.6161 12.1506 12.6885 11.9443 12.6885C11.7379 12.6885 11.5623 12.6161 11.4173 12.4713L6.34425 7.398Z"
      fill="currentColor"
      transform="translate(5.65575 5.65575)"
    />
  </svg>
);

const MobileSelectedIcon = () => (
  <svg
    aria-hidden="true"
    className={styles.mobileSelectedIcon}
    data-eli-part="listing-sort-selected-icon"
    focusable="false"
    viewBox="0 0 20 20"
  >
    <path
      d="M6.73396 9.70521L4.79812 7.76917C4.68271 7.65389 4.53764 7.59486 4.36292 7.59208C4.18833 7.58944 4.04062 7.64847 3.91979 7.76917C3.7991 7.89 3.73875 8.03639 3.73875 8.20833C3.73875 8.38028 3.7991 8.52667 3.91979 8.6475L6.20667 10.9344C6.35736 11.0849 6.53312 11.1602 6.73396 11.1602C6.93479 11.1602 7.11056 11.0849 7.26125 10.9344L11.8975 6.29812C12.0128 6.18271 12.0718 6.03764 12.0746 5.86292C12.0772 5.68833 12.0182 5.54063 11.8975 5.41979C11.7767 5.2991 11.6303 5.23875 11.4583 5.23875C11.2864 5.23875 11.14 5.2991 11.0192 5.41979L6.73396 9.70521ZM7.91812 15.8333C6.82312 15.8333 5.79389 15.6256 4.83042 15.21C3.86694 14.7944 3.02889 14.2305 2.31625 13.5181C1.60361 12.8058 1.03937 11.9681 0.623541 11.005C0.207847 10.0419 0 9.01299 0 7.91812C0 6.82312 0.207778 5.79389 0.623333 4.83042C1.03889 3.86694 1.60285 3.02889 2.31521 2.31625C3.02757 1.60361 3.86528 1.03937 4.82833 0.623541C5.79139 0.207847 6.82035 0 7.91521 0C9.01021 0 10.0394 0.207777 11.0029 0.623333C11.9664 1.03889 12.8044 1.60285 13.5171 2.31521C14.2297 3.02757 14.794 3.86528 15.2098 4.82833C15.6255 5.79139 15.8333 6.82035 15.8333 7.91521C15.8333 9.01021 15.6256 10.0394 15.21 11.0029C14.7944 11.9664 14.2305 12.8044 13.5181 13.5171C12.8058 14.2297 11.9681 14.794 11.005 15.2098C10.0419 15.6255 9.01299 15.8333 7.91812 15.8333Z"
      fill="currentColor"
      transform="translate(2.08335 2.08335)"
    />
  </svg>
);

export const RealtyFilterSortMobile = () => {
  const { t } = useTranslations();
  const { SORT_VALUES, applySort, filter } = useRealtyStore();
  const selectedValue = `${filter.orderBy}:${filter.order}`;
  const titleRef = useRef<HTMLHeadingElement>(null);
  const titleId = useId();
  const [open, setOpen] = useState(false);
  const commitSort = (value: string) => {
    const [orderBy, order] = value.split(':') as [RealtyOrderBy, RealtyOrder];
    applySort(orderBy, order);
    setOpen(false);
  };
  const handleSortKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    const options = Array.from(
      event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>(
        '[data-eli-part="listing-sort-option"]',
      ) ?? [],
    );
    const currentIndex = options.indexOf(event.currentTarget);
    let nextIndex: number | undefined;

    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
      nextIndex = (currentIndex + 1) % options.length;
    } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
      nextIndex = (currentIndex - 1 + options.length) % options.length;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = options.length - 1;
    }

    if (nextIndex === undefined || options.length === 0) return;

    event.preventDefault();
    options[nextIndex]?.focus();
    options[nextIndex]?.click();
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="secondary"
          className={listingStyles.mobileSortTrigger}
          aria-label={t('text1421')}
          data-eli-part="listing-sort-trigger"
        >
          <MobileSortIcon />
        </Button>
      </SheetTrigger>
      <SheetContent
        side="bottom"
        showCloseIcon={false}
        className={styles.mobileSortSheet}
        data-eli-component="realty-sort-sheet"
        aria-describedby={undefined}
        onOpenAutoFocus={event => {
          event.preventDefault();
          titleRef.current?.focus({ preventScroll: true });
        }}
      >
        <div className={styles.mobileSortHeader}>
          <SheetTitle
            ref={titleRef}
            tabIndex={-1}
            className={styles.mobileSortTitle}
            data-eli-part="listing-sort-title"
          >
            <span id={titleId} data-eli-part="listing-sort-title-label">
              {t('text1421')}
            </span>
          </SheetTitle>
          <SheetClose
            className={styles.mobileSortClose}
            aria-label={t('text1855')}
            data-eli-part="listing-sort-close"
          >
            <MobileCloseIcon />
          </SheetClose>
        </div>
        <div
          className={styles.mobileSortOptions}
          data-eli-part="listing-sort-options"
          role="radiogroup"
          aria-labelledby={titleId}
        >
          {SORT_VALUES.map(value => {
            const selected = selectedValue === value.value;
            return (
              <button
                type="button"
                key={value.value}
                className={styles.mobileSortOption}
                aria-checked={selected}
                role="radio"
                tabIndex={selected ? 0 : -1}
                data-eli-part="listing-sort-option"
                data-sort-value={value.value}
                data-selected={selected ? 'true' : 'false'}
                onClick={() => commitSort(value.value)}
                onKeyDown={handleSortKeyDown}
              >
                <span>{t(value.label)}</span>
                {selected && <MobileSelectedIcon />}
              </button>
            );
          })}
        </div>
      </SheetContent>
    </Sheet>
  );
};
